import { useRef, useState } from 'react';
import { useM, useQ } from './hooks';

/**
 * Sorting of lists by a column (request 14 of round 4). The choice is personal: it is stored with the account
 * (preference "table.sort", one entry per list) and follows the person to every device; nobody else sees it.
 * A click sorts by the column (dates: earliest first), a second click turns the order round, a third one goes back to
 * the order of the page.
 */
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: string;
  dir: SortDir;
}
type Value = string | number | null | undefined;

export function useTableSort<T>(tableId: string, accessors: Record<string, (row: T) => Value>) {
  const pref = useQ('prefs.get', { keys: ['table.sort'] }, { staleTime: 5 * 60_000 });
  const save = useM('prefs.set', { invalidate: ['prefs.'] });
  const stored = (pref.data?.['table.sort'] ?? {}) as Record<string, SortState>;
  // changes of this visit win over the stored value until it is written back (several clicks in a row)
  const pending = useRef<Record<string, SortState | null>>({});
  const [, force] = useState(0);
  const sort: SortState | null = tableId in pending.current ? pending.current[tableId] : (stored[tableId] ?? null);
  const valid = sort && accessors[sort.key] ? sort : null;
  const toggle = (key: string) => {
    const next: SortState | null = !valid || valid.key !== key ? { key, dir: 'asc' } : valid.dir === 'asc' ? { key, dir: 'desc' } : null;
    pending.current = { ...pending.current, [tableId]: next };
    force((x) => x + 1);
    const all: Record<string, SortState> = { ...stored };
    for (const [id, s] of Object.entries(pending.current)) {
      if (s) all[id] = s;
      else delete all[id];
    }
    save.mutate({ key: 'table.sort', value: all });
  };
  const apply = (rows: readonly T[]): T[] => {
    if (!valid) return [...rows];
    const get = accessors[valid.key];
    const factor = valid.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = get(a);
      const y = get(b);
      // empty values last in both directions
      const ex = x === null || x === undefined || x === '';
      const ey = y === null || y === undefined || y === '';
      if (ex || ey) return ex === ey ? 0 : ex ? 1 : -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * factor;
      return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) * factor;
    });
  };
  return { sort: valid, toggle, apply };
}
